import {
  ContensisQueryOrderBy,
  IExpression,
  ILogicalExpression,
  Op,
  OrderBy,
  VersionStatus,
} from 'contensis-core-api';
import { DataFormats, Fields } from '~/models/Enums';
import {
  CustomWhereClause,
  FieldOperators,
  SearchQueryFilter,
  WeightedSearchField,
} from '../models/SearchQuery';

export const fieldExpression = (
  field: string | string[],
  value: any,
  operator: FieldOperators = 'equalTo',
  weight?: number
): IExpression[] => {
  if (!field || !value) return [];
  if (Array.isArray(field))
    // If an array of fieldIds have been provided, call self for each fieldId
    // to generate expressions that are combined with an 'or' operator
    return [
      Op.or(
        ...field
          .map((fieldId) => fieldExpression(fieldId, value, operator, weight))
          .flat()
      ),
    ];
  if (operator === 'between') return between(field, value);
  if (Array.isArray(value)) return equalToOrIn(field, value, operator);
  else
    return !weight
      ? equalToOrIn(field, value, operator)
      : [equalToOrIn(field, value, operator)[0].weight(weight)];
};

export const contentTypeIdExpression = (
  contentTypeIds: string[] = [],
  webpageTemplates?: string[],
  assetTypes?: string[]
) => {
  const expressions: ILogicalExpression[] = [];
  if (!contentTypeIds && !webpageTemplates && !assetTypes) return expressions;

  if (contentTypeIds && contentTypeIds.length > 0) {
    expressions.push(
      ...dataFormatWithContentTypesExpression(contentTypeIds, DataFormats.entry)
    );
  }

  if (webpageTemplates && webpageTemplates.length > 0) {
    expressions.push(
      ...dataFormatWithContentTypesExpression(
        webpageTemplates,
        DataFormats.webpage
      )
    );
  }

  if (assetTypes && assetTypes.length > 0) {
    expressions.push(
      ...dataFormatWithContentTypesExpression(assetTypes, DataFormats.asset)
    );
  }

  if (expressions.length > 1) return [Op.or(...expressions)];

  return expressions;
};

export const filterExpressions = (filters?: SearchQueryFilter[]) => {
  if (!filters) return [];
  const expressions: IExpression[] = [];

  filters.map((param) => {
    expressions.push(
      ...fieldExpression(param.key, param.value, param.operator || 'in')
    );
  });

  return expressions;
};

export const dataFormatExpression = (dataFormats?: DataFormats[]) =>
  dataFormats ? fieldExpression(Fields.sys.dataFormat, dataFormats) : [];

export const dataFormatWithContentTypesExpression = (
  contentTypeIds: string[] = [],
  dataFormat = DataFormats.entry
) => {
  if (contentTypeIds && contentTypeIds.length > 0) {
    /**
     * We have an array of contentTypeIds some may be prefixed with a "!"
     * to indicate this is a "not" expression
     */
    const withContentTypeIds = contentTypeIds.filter((c) => !c.startsWith('!'));
    const notContentTypeIds = contentTypeIds
      .filter((c) => c.startsWith('!'))
      .map((id) => id.substring(1));

    const andExpr = Op.and();

    const dataFormatExpr = fieldExpression(
      Fields.sys.dataFormat,
      dataFormat
    )[0];

    const withExpr = fieldExpression(
      Fields.sys.contentTypeId,
      withContentTypeIds
    )[0];

    const notExpr = Op.not(
      fieldExpression(Fields.sys.contentTypeId, notContentTypeIds)[0]
    );

    andExpr.add(dataFormatExpr);
    if (withContentTypeIds.length > 0 && withExpr) andExpr.add(withExpr);
    if (notContentTypeIds.length > 0 && notExpr) andExpr.add(notExpr);

    return [andExpr];
  }
  return [];
};

export const featuredResultsExpression = ({
  contentTypeId,
  fieldId,
  fieldValue = true,
}: {
  contentTypeId?: string | string[];
  fieldId?: string | string[];
  fieldValue?: boolean;
} = {}) => {
  const expressions = [] as IExpression[];

  if (contentTypeId) {
    expressions.push(
      ...contentTypeIdExpression(
        Array.isArray(contentTypeId) ? contentTypeId : [contentTypeId]
      )
    );
  }

  if (fieldId && fieldValue) {
    expressions.push(...fieldExpression(fieldId, fieldValue));
  }

  return expressions;
};
export const entryTitlesExpression = (entryTitles: string[] = []) =>
  fieldExpression(Fields.entryTitle, entryTitles);

export const includePathsExpression = (includePaths: string[] = []) =>
  fieldExpression(Fields.sys.uri, includePaths, 'startsWith');

export const languagesExpression = (languages: string[] = []) =>
  fieldExpression(Fields.sys.language, languages);

export const defaultExpressions = (versionStatus: VersionStatus) => {
  return [Op.equalTo(Fields.sys.versionStatus, versionStatus)];
};

export const includeIdsExpression = (includeIds?: string[]) => {
  if (Array.isArray(includeIds) && includeIds.length > 0) {
    return fieldExpression(Fields.sys.id, includeIds);
  } else return [];
};

export const excludeIdsExpression = (excludeIds?: string[]) => {
  if (Array.isArray(excludeIds) && excludeIds.length > 0) {
    const [expr] = includeIdsExpression(excludeIds);
    return [Op.not(expr)];
  } else return [];
};

export const orderByExpression = (orderBy?: string[]) => {
  let expression: ContensisQueryOrderBy | undefined;
  if (orderBy && orderBy.length > 0) {
    expression = OrderBy;
    for (const ob of orderBy) {
      expression = ob.startsWith('-')
        ? expression?.desc(ob.substring(1))
        : expression?.asc(ob);
    }
  }
  return expression as ContensisQueryOrderBy;
};

const equalToOrIn = (
  field: string,
  value: string | string[],
  operator: FieldOperators = 'equalTo'
) => {
  if (value.length === 0) return [];
  if (Array.isArray(value)) {
    if (operator === 'equalTo') return [Op.in(field, ...value)];
    return [
      Op.or(
        ...value.map((innerValue) => {
          switch (operator) {
            case 'between':
            case 'distanceWithin':
              // Not implemented
              return Op.equalTo(field, innerValue);
            case 'freeText':
              // TODO: Potentially needs further implementation of new options
              return Op[operator](field, innerValue, false, undefined);
            default:
              return Op[operator](field, innerValue);
          }
        })
      ),
    ];
  }
  switch (operator) {
    case 'between':
    case 'distanceWithin':
      // Not implemented
      return [Op.equalTo(field, value)];
    case 'freeText':
      // TODO: Potentially needs further implementation of new options
      return [Op[operator](field, value, false, undefined)];
    default:
      return [Op[operator](field, value)];
  }
};

const between = (field: string, value: string | string[]) => {
  const handle = (betweenValue: string) => {
    const valArr = betweenValue.split('-');
    if (valArr.length > 1) {
      const [minimum, maximum = null] = betweenValue.split('-');
      return Op.between(field, minimum, maximum);
    } else {
      console.log(
        `[search] You have supplied only one value to a "between" operator which must have two values. Your supplied value "${valArr.length && valArr[0]
        }" has been discarded.`
      );
      return false;
    }
  };
  if (value.length === 0) return [];
  if (Array.isArray(value))
    return [
      Op.or(
        ...(value.map(handle).filter((bc) => bc !== false) as IExpression[])
      ),
    ];

  // const valArr = value.split('-');
  const op = handle(value);
  return op ? [op] : []; // valArr.length > 1 ? [Op.between(field, ...value.split('-'))] : [];
};

/**
 * Accept HTTP style objects and map them to
 * their equivalent JS client "Op" expressions
 * @param {array} where the where array as you'd provide it to the HTTP API
 * @returns {array} array of constructed Delivery API Operators
 */
export const customWhereExpressions = (where?: CustomWhereClause) => {
  if (!where || !Array.isArray(where)) return [];

  // Map each clause inside the where array
  return where
    .map((clause) => {
      let expression: IExpression | undefined;
      // Map through each property in the clause so we can
      // capture the values required and reconstruct them as
      // a Delivery API expression
      let operator: 'field' | 'not' | 'and' | 'or' | FieldOperators;
      Object.keys(clause).map((key: string, idx) => {
        // The clause may contain only one key
        if (idx === 0) operator = key as 'not' | 'and' | 'or';

        const field: string = (clause as any).field;
        const value = (clause as any)[
          Object.keys(clause).find((k) => !['field', 'weight'].includes(k)) ||
          ''
        ];
        const weight: number = (clause as any).weight;

        if (idx === 0) {
          if (operator === 'and' || operator === 'or') {
            // These are array expressions so we can call ourself recursively
            // to map these inner values to expressions
            const recurseExpr = customWhereExpressions(
              (clause as any)[operator]
            ) as unknown as IExpression[];
            expression = Op[operator](...recurseExpr);
          }

          if (['not'].includes(operator)) {
            // A 'not' expression is an object with only one inner field and inner operator
            Object.keys(value).map((notKey, notIdx) => {
              const innerOperator = notKey as Exclude<
                FieldOperators,
                'between' | 'distanceWithin' // these are not handled
              >;
              const innerValue = value[notKey];
              const innerField = value.field;
              // Map the expression when we've looped and scoped to
              // the second property inside the clause
              if (notIdx === 1) {
                expression = Op.not(Op[innerOperator](innerField, innerValue));
              }
            });
          }
        }
        // Map the expression when we've looped and scoped to
        // the second property inside the clause
        operator = Object.keys(clause).find(
          (clauseKey) => !['field', 'weight'].includes(clauseKey)
        ) as FieldOperators;

        if (
          idx === 1 &&
          // operator !== 'and' &&
          // operator !== 'or' &&
          operator !== 'between' &&
          operator !== 'distanceWithin'
        ) {
          expression =
            operator === 'freeText' || operator === 'contains'
              ? Op[operator](field, value)
              : operator === 'in'
                ? Op[operator](field, ...value)
                : operator === 'exists'
                  ? Op[operator](field, value)
                  : Op[operator](field, value);
          if (typeof weight === 'number')
            expression = expression.weight(weight);
        }
      });
      return expression;
    })
    .filter((e) => !!e) as IExpression[];
};

export const termExpressions = (
  searchTerm?: string,
  weightedSearchFields?: WeightedSearchField[]
) => {
  if (searchTerm && weightedSearchFields && weightedSearchFields.length > 0) {
    // Extract any phrases in quotes to array
    const quotedPhrases = extractQuotedPhrases(searchTerm);

    // Modify the search term to remove any quoted phrases to leave any remaining terms
    let modifiedSearchTerm = searchTerm;
    quotedPhrases.forEach(
      (qp) =>
      (modifiedSearchTerm = modifiedSearchTerm
        .replace(qp, '')
        .replace('""', '')
        .trim())
    );

    // Push to the operators array to include in the query
    const operators = [] as IExpression[];

    // Helper functions to generate Op expressions
    const containsOp = (f: WeightedSearchField, term: string) =>
      fieldExpression(
        f.fieldId,
        fixFreeTextForElastic(term),
        'contains',
        f.weight
      );
    const freeTextOp = (f: WeightedSearchField, term: string) =>
      fieldExpression(
        f.fieldId,
        fixFreeTextForElastic(term),
        'freeText',
        f.weight
      );

    // For each weighted search field
    weightedSearchFields.forEach((wsf) => {
      // Push to field operators
      const fieldOperators: IExpression[] = [];

      // Add operator expressions for modified search term
      if (modifiedSearchTerm) {
        if (
          [Fields.keywords, Fields.sys.filename, Fields.sys.uri].includes(
            wsf.fieldId
          )
        ) {
          fieldOperators.push(...containsOp(wsf, modifiedSearchTerm));
        } else {
          if ([Fields.entryTitle].includes(wsf.fieldId)) {
            fieldOperators.push(
              Op.or(
                ...containsOp(wsf, modifiedSearchTerm),
                ...freeTextOp(wsf, modifiedSearchTerm)
              )
            );
          } else {
            fieldOperators.push(...freeTextOp(wsf, modifiedSearchTerm));
          }
        }
      }

      // Add operator expressions for any quoted phrases
      quotedPhrases.forEach((qp) =>
        fieldOperators.push(...containsOp(wsf, qp))
      );

      // If we are using multiple operators for a field we will
      // wrap each field inside an And operator so we will match
      // all terms/phrases rather than any terms/phrases
      if (fieldOperators.length > 1) {
        operators.push(Op.and(...fieldOperators));
      } else {
        operators.push(...fieldOperators);
      }
    });

    // Wrap operators in an Or operator
    return [
      Op.or()
        .addRange(operators)
        .add(Op.freeText(Fields.searchContent, searchTerm)),
    ] as ILogicalExpression[];
  } else if (searchTerm) {
    // Searching without weightedSearchFields defined will fall back
    // to a default set of search fields with arbritary weights set.
    return [
      Op.or(
        Op.equalTo(Fields.entryTitle, searchTerm).weight(10),
        Op.freeText(Fields.entryTitle, searchTerm).weight(2),
        Op.freeText(Fields.entryDescription, searchTerm).weight(2),
        Op.contains(Fields.keywords, searchTerm).weight(2),
        Op.contains(Fields.sys.uri, searchTerm).weight(2),
        Op.contains(Fields.sys.allUris, searchTerm),
        Op.freeText(Fields.searchContent, searchTerm)
      ),
    ];
  } else {
    return [];
  }
};

export const extractQuotedPhrases = (searchTerm: string): string[] => {
  const pattern = new RegExp(
    /(?=["'])(?:"[^"\\]*(?:\\[\s\S][^"\\]*)*"|'[^'\\]*(?:\\[\s\S][^'\\]*)*')/gm
  );

  return (searchTerm.match(pattern) || []).map((match) =>
    match.replace(/"/g, '')
  );
};
export function fixFreeTextForElastic(s: string): string {
  const illegalChars = [
    '>',
    '<',
    '=',
    '|',
    '!',
    '{',
    '}',
    '[',
    ']',
    '^',
    '~',
    '*',
    '?',
    ':',
    '\\',
    '/',
  ];

  const illegalRegEx = new RegExp(
    illegalChars.map((c) => '\\' + c).join('|'),
    'g'
  );
  s = s.replace(illegalRegEx, '');
  // s = s.replace(encodedRegEx, ''); // (m) => '\\\\' + m);

  return s;
}
